import { useCallback, useEffect, useState } from "react";
import { mdiTrayArrowDown, mdiRefresh, mdiAlertCircleOutline, mdiPaperclip } from "@mdi/js";
import Icon from "@mdi/react";

import { LoadingSpinnerSVG, VisibilitySVGIcon } from "components/SVGIcons";
import { IAttachment } from "models/HaulPay";
import { axios } from "services/middleware";
import { showErrorMessage } from "helpers/toastMessage";
import Button from "components/Button";

interface IAttachmentItemProps {
  label: string;
  data?: IAttachment | null;
  url?: string;
  fetchDocument?: () => Promise<any> | null;
}

export default function InvoiceDownloader({
  label,
  data,
  url,
  fetchDocument
}: Readonly<IAttachmentItemProps>) {
  const [generatedAttachment, setGeneratedAttachment] = useState<IAttachment>(data as IAttachment);
  const [isFetching, setIsFetching] = useState(false);
  const [errorFetching, setErrorFetching] = useState(false);

  useEffect(() => {
    data?.download_url && setGeneratedAttachment(data);
  }, [data?.download_url]);

  const handlerGenerateAttachmentFromUrl = useCallback(async (url: string) => {
    try {
      setIsFetching(true);
      const { data } = await axios.get(url);
      setGeneratedAttachment(data);
    } catch (error) {
      showErrorMessage("Error creating File.");
      setErrorFetching(true);
    } finally {
      setIsFetching(false);
      setTimeout(() => setErrorFetching(false), 10000);
    }
  }, []);

  const handlerGenerateAttachmentFromFunction = useCallback(async () => {
    if (!fetchDocument) return;
    try {
      setIsFetching(true);
      const { data } = await fetchDocument();
      setGeneratedAttachment(data);
    } catch (error) {
      showErrorMessage("Error creating File.");
      setErrorFetching(true);
    } finally {
      setIsFetching(false);
      setTimeout(() => setErrorFetching(false), 10000);
    }
  }, []);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="flex flex-col items-center justify-center p-3 border rounded-lg hover:bg-[rgb(var(--accent))] transition-colors !h-24">
        <div className="text-xs font-medium mb-2 text-center text-[rgb(var(--text))]">{label}</div>
        {errorFetching ? (
          <a target="_blank" rel="noopener noreferrer" className="!text-red-800">
            <Icon path={mdiAlertCircleOutline} />
          </a>
        ) : !generatedAttachment?.preview_url || isFetching ? (
          <a
            onClick={() =>
              (url && handlerGenerateAttachmentFromUrl(url)) ||
              (fetchDocument && handlerGenerateAttachmentFromFunction())
            }
            target="_blank"
            rel="noopener noreferrer">
            {isFetching ? (
              <LoadingSpinnerSVG className="inline size-3 animate-spin text-text fill-primary mx-auto" />
            ) : (
              <Icon path={mdiPaperclip} size={1} />
            )}
          </a>
        ) : (
          <>
            <div className="flex flex-row gap-3">
              <a href={generatedAttachment?.preview_url} target="_blank" rel="noopener noreferrer">
                <VisibilitySVGIcon />
              </a>
              <a href={generatedAttachment?.download_url} target="_blank" rel="noopener noreferrer">
                <Icon path={mdiTrayArrowDown} />
              </a>
            </div>
            {Boolean(url) && (
              <a
                onClick={() => url && handlerGenerateAttachmentFromUrl(url)}
                target="_blank"
                rel="noopener noreferrer">
                <Icon path={mdiRefresh} />
              </a>
            )}
          </>
        )}
      </Button>
    </>
  );
}
